/**
 * Desktop alerts for funds into Crown wallets.
 *
 * Switched on per wallet (the bell in a Crown wallet's window on the Bank
 * Payments Balances tab); the choice is saved against the person, so it
 * follows them to any browser where notifications are allowed.
 *
 * While the platform is open in any tab (even minimised) and this person
 * has alerts on for at least one wallet, one tab (Web Locks) asks every
 * 20 seconds for new funds in on those wallets and shows a desktop
 * notification for each: "Crown: 250.50 GBP in · Acme Ltd". Clicking it
 * brings the platform forward on Bank Payments. The tick runs in a small
 * Web Worker, as browsers slow background-tab timers.
 *
 * The server notices funds when a wallet's balance rises between its
 * once-a-minute pulls from Crown.
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { crownApi, type CrownIncoming } from "@/lib/crownApi";

const LOCK_NAME = "mfx-crown-alerts";
const POLL_MS = 20_000;
const RESUME_MAX_MS = 10 * 60_000;
export const CROWN_SUBS_KEY = ["bp", "crown", "subscriptions"];

const sinceKey = (userId: string) => `mfx_crown_alerts_since_${userId}`;

export function notificationsAllowed(): boolean {
  return typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted";
}

/** Ask the browser for permission if needed. True when notifications can show. */
export async function askNotifications(): Promise<"granted" | "denied" | "default" | "unsupported"> {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  return Notification.requestPermission();
}

const fmt = (n: number, ccy: string) =>
  `${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`.trim();

function show(e: CrownIncoming, onClick: () => void) {
  const who = e.sfAccountName || e.sfBankAccountName || `wallet ${e.walletId}`;
  const n = new Notification(`Crown: ${fmt(e.amount, e.currency)} in · ${who}`, {
    body: `${e.sfBankAccountName || `Wallet ${e.walletId}`} · balance now ${e.newBalance == null ? "—" : fmt(e.newBalance, e.currency)}`,
    tag: `mfx-crown-${e.id}`,
  });
  n.onclick = () => { window.focus(); onClick(); n.close(); };
}

function makeTicker(onTick: () => void): () => void {
  try {
    const src = `setInterval(function(){postMessage(0)},${POLL_MS});`;
    const url = URL.createObjectURL(new Blob([src], { type: "text/javascript" }));
    const worker = new Worker(url);
    worker.onmessage = onTick;
    return () => { worker.terminate(); URL.revokeObjectURL(url); };
  } catch {
    const id = window.setInterval(onTick, POLL_MS);
    return () => window.clearInterval(id);
  }
}

/** Mounted once in the staff app, for people with the Balances tab. */
export function CrownAlerts({ user, enabled }: { user: any; enabled: boolean }) {
  const navigate = useNavigate();
  const userId = String(user?.id || "");
  const subs = useQuery({
    queryKey: CROWN_SUBS_KEY,
    queryFn: crownApi.subscriptions,
    enabled: enabled && !!userId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const active = enabled && !!userId && (subs.data?.length ?? 0) > 0 && notificationsAllowed();

  useEffect(() => {
    if (!active) return;
    let stopped = false;
    let release: (() => void) | null = null;
    let stopTicker: (() => void) | null = null;

    const run = () => {
      // Pick up where the last tab left off, unless that was a while ago
      // (the platform was closed): then start fresh, no backlog.
      let since: number | null = null;
      try {
        const saved = JSON.parse(localStorage.getItem(sinceKey(userId)) || "null");
        if (saved && Number.isFinite(saved.id) && Date.now() - Number(saved.at) < RESUME_MAX_MS) since = saved.id;
      } catch { /* private mode */ }
      let inFlight = false;
      const check = async () => {
        if (stopped || inFlight) return;
        inFlight = true;
        try {
          const res = await crownApi.incoming(since);
          for (const e of res.events) show(e, () => navigate("/bank-payments"));
          since = res.latestId;
          try { localStorage.setItem(sinceKey(userId), JSON.stringify({ id: res.latestId, at: Date.now() })); } catch { /* ignore */ }
        } catch { /* offline or signed out: next tick */ }
        finally { inFlight = false; }
      };
      check();
      stopTicker = makeTicker(check);
    };

    const locks = (navigator as any).locks;
    if (locks?.request) {
      locks.request(LOCK_NAME, () => new Promise<void>((resolve) => {
        release = resolve;
        if (stopped) { resolve(); return; }
        run();
      })).catch(() => { /* ignore */ });
    } else {
      run();
    }
    return () => { stopped = true; stopTicker?.(); release?.(); };
  }, [active, userId, navigate]);

  return null;
}
